import { useState, useEffect, useCallback } from "react";
import {
  Plus, Trash2, Search, X, Check,
  AlertTriangle, Image as ImageIcon, Filter, Loader2, Eye, ZoomIn,
} from "lucide-react";
import Pagination, { usePagination } from "../shared/Pagination";
import { useBanners } from "../context/BannersContext";

/* ─────────────────────────────────────────────
   HELPERS
   API: { id, banner (filename), title (nullable), status ("active"|"inactive") }
───────────────────────────────────────────── */
const toActive = (status) => status === "active";
const toStatus = (active) => (active ? "active" : "inactive");

/* ─────────────────────────────────────────────
   TOAST
───────────────────────────────────────────── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

function ToastContainer({ toasts }) {
  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium border animate-slide-up
            ${t.type === "success"
              ? "bg-[#f0faf4] border-[#bbf7d0] text-[#1a6b3c]"
              : t.type === "warning"
              ? "bg-amber-50 border-amber-200 text-amber-700"
              : "bg-red-50 border-red-200 text-red-700"}`}
        >
          {t.type === "success" && <Check size={15} className="text-[#22A65A] flex-shrink-0" />}
          {t.type === "warning" && <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />}
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MODAL PRIMITIVES
───────────────────────────────────────────── */
function Modal({ open, onClose, children, maxW = "max-w-md" }) {
  const isSidePanel = maxW !== "max-w-sm";
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  if (isSidePanel) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col overflow-hidden">
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxW} border border-gray-100`}>{children}</div>
    </div>
  );
}

function ModalHeader({ title, icon: Icon, onClose }) {
  return (
    <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
      <h2 className="text-lg font-semibold text-white tracking-tight">{title}</h2>
      <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
        <X size={16} className="text-white" />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   STATUS TOGGLE
───────────────────────────────────────────── */
function StatusToggle({ active, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200 flex-shrink-0 disabled:opacity-50
        ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}`}
    >
      <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${active ? "translate-x-5" : "translate-x-1"}`} />
    </button>
  );
}

/* ─────────────────────────────────────────────
   VIEW MODAL
───────────────────────────────────────────── */
function ViewModal({ open, onClose, item, getImageUrl }) {
  if (!item) return null;
  return (
    <Modal open={open} onClose={onClose} maxW="max-w-lg">
      <ModalHeader title="Banner Preview" icon={Eye} onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <div className="rounded-xl overflow-hidden border border-[#e8e0d0] bg-gray-50">
          <img
            src={getImageUrl(item.banner)}
            alt={item.title || `Banner #${item.id}`}
            className="w-full object-cover max-h-64"
            onError={(e) => { e.target.src = "https://placehold.co/600x200?text=No+Image"; }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              {item.title ? (
                <p className="text-sm font-semibold text-gray-800">{item.title}</p>
              ) : (
                <p className="text-sm text-gray-400 italic">No title</p>
              )}
             
            </div>
            <span className={`flex-shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full
              ${toActive(item.status)
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-500 border border-red-200"}`}>
              {toActive(item.status) ? "Active" : "Inactive"}
            </span>
          </div>
          {/* <p className="text-xs text-gray-400">Banner ID: {item.id}</p> */}
        </div>
      </div>
      <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
        <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 hover:bg-white transition-all active:scale-95">
          Close
        </button>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   DELETE MODAL
───────────────────────────────────────────── */
function DeleteModal({ open, onClose, onConfirm, item }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => { if (open) { setLoading(false); setError(""); } }, [open]);

  const handleConfirm = async () => {
    setLoading(true);
    try { await onConfirm(item?.id); onClose(); }
    catch (err) { setError(err.message || "Failed to delete banner"); setLoading(false); }
  };

  // display name: title if present, else filename, else id
  const displayName = item?.title || item?.banner || `#${item?.id}`;

  return (
    <Modal open={open} onClose={onClose} maxW="max-w-sm">
      <div className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={22} className="text-red-500" />
        </div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Delete Banner?</h3>
        <p className="text-sm text-gray-500 mb-2">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-gray-700">"{displayName}"</span>? This cannot be undone.
        </p>
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
        <div className="flex gap-3 mt-4">
          <button onClick={onClose} disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all active:scale-95 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <><Trash2 size={14} /> Delete</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   ADD MODAL
───────────────────────────────────────────── */
function AddModal({ open, onClose, onSave }) {
  const [title, setTitle]       = useState("");
  const [active, setActive]     = useState(true);
  const [error, setError]       = useState({});
  const [loading, setLoading]   = useState(false);
  const [preview, setPreview]   = useState(null);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (open) { setTitle(""); setActive(true); setError({}); setLoading(false); setPreview(null); setImageFile(null); }
  }, [open]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
    setError((er) => ({ ...er, image: "" }));
  };

  const handleSave = async () => {
    const errs = {};
    if (!imageFile) errs.image = "Banner image is required";
    if (Object.keys(errs).length) { setError(errs); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("banner", imageFile);
      fd.append("status", toStatus(active));
      // title is optional — only send if filled
      if (title.trim()) fd.append("title", title.trim());
      await onSave(fd);
      onClose();
    } catch (err) {
      setError({ general: err.message || "Failed to save banner" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} maxW="max-w-lg">
      <ModalHeader title="Add Banner" icon={Plus} onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

        {/* Title — optional */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Banner Title <span className="text-gray-300 font-normal normal-case">(optional)</span>
          </label>
          <input
            autoFocus
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Summer Sale 2025"
            className="w-full h-10 px-3 rounded-xl border border-[#e2ddd4] hover:border-[#c5bfb5] focus:border-teal-500 focus:ring-2 focus:ring-teal-100 text-sm bg-white text-gray-800 placeholder-gray-400 outline-none transition-all"
          />
        </div>

        {/* Image Upload */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Banner Image <span className="text-red-400">*</span>
          </label>
          <label className={`flex flex-col items-center justify-center gap-2 h-36 rounded-xl border-2 border-dashed cursor-pointer transition-all overflow-hidden
            ${error.image ? "border-red-300 bg-red-50" : "border-[#e2ddd4] bg-white hover:border-teal-400 hover:bg-teal-50/30"}`}>
            {preview ? (
              <img src={preview} alt="preview" className="h-full w-full object-cover" />
            ) : (
              <>
                <ImageIcon size={22} className="text-gray-300" />
                <span className="text-xs text-gray-400">Click to upload image</span>
                <span className="text-[11px] text-gray-300">JPG, PNG, WEBP supported</span>
              </>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
          </label>
          {preview && (
            <button type="button" onClick={() => { setPreview(null); setImageFile(null); }}
              className="text-[11px] text-red-400 hover:text-red-600 self-start">
              Remove image
            </button>
          )}
          {error.image && <p className="text-xs text-red-500">{error.image}</p>}
        </div>

        {/* Status */}
        <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#e2ddd4] bg-white">
          <div>
            <p className="text-sm font-medium text-gray-700">Status</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {active ? "This banner is currently active" : "This banner is currently inactive"}
            </p>
          </div>
          <button type="button" onClick={() => setActive((v) => !v)}
            className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200
              ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}`}>
            <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${active ? "translate-x-5" : "translate-x-1"}`} />
          </button>
        </div>

        {error.general && <p className="text-xs text-red-500">{error.general}</p>}
      </div>

      <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-100">
        <button onClick={onClose} disabled={loading}
          className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={handleSave} disabled={loading}
          className="px-5 py-2.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium transition-all active:scale-95 flex items-center gap-2 shadow-sm disabled:opacity-60">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} /> Save Banner</>}
        </button>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   BANNERS PAGE
───────────────────────────────────────────── */
export default function BannersPage() {
  const {
    banners,
    bannersLoading,
    addBanner,
    deleteBanner,
    updateBannerStatus,
    getBannerImageUrl,
  } = useBanners();

  const [search, setSearch]             = useState("");
  const [sortBy, setSortBy]             = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filtersOpen, setFiltersOpen]   = useState(false);
  const [addOpen, setAddOpen]           = useState(false);
  const [viewItem, setViewItem]         = useState(null);
  const [delItem, setDelItem]           = useState(null);
  const [togglingId, setTogglingId]     = useState(null);
  const { toasts, show: toast }         = useToast();

  const activeCount   = banners.filter((i) => i.status === "active").length;
  const inactiveCount = banners.length - activeCount;

  /* ── Filter + Sort ── */
  let filtered = banners.filter((i) => {
    const q = search.toLowerCase();
    const matchSearch =
      String(i.id).includes(q) ||
      (i.banner || "").toLowerCase().includes(q) ||
      (i.title || "").toLowerCase().includes(q);
    const matchStatus =
      statusFilter === "all" ? true :
      statusFilter === "active" ? i.status === "active" :
      i.status === "inactive";
    return matchSearch && matchStatus;
  });
  if (sortBy === "title_asc")  filtered = [...filtered].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  if (sortBy === "title_desc") filtered = [...filtered].sort((a, b) => (b.title || "").localeCompare(a.title || ""));
  if (sortBy === "active")     filtered = [...filtered].sort((a, b) => (b.status === "active" ? 1 : 0) - (a.status === "active" ? 1 : 0));
  if (sortBy === "inactive")   filtered = [...filtered].sort((a, b) => (a.status === "active" ? 1 : 0) - (b.status === "active" ? 1 : 0));

  const { page: banPage, pageSize: banSize, setPage: setBanPage, setPageSize: setBanSize, totalPages: banTotalPages, totalItems: banTotal, paginated: paginatedBan } = usePagination(filtered, 10);

  /* ── Handlers ── */
  const handleAdd = async (formData) => {
    await addBanner(formData);
    toast("Banner added successfully");
  };

  const handleDelete = async (id) => {
    await deleteBanner(id);
    toast("Banner deleted", "warning");
  };

  const handleToggle = async (item) => {
    if (togglingId === item.id) return;
    setTogglingId(item.id);
    const newStatus = toStatus(!toActive(item.status));
    try {
      await updateBannerStatus(item.id, newStatus);
      toast(`Banner marked ${newStatus}`);
    } catch {
      toast("Failed to update status", "error");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <>
      <style>{`
        @keyframes modalIn { from { opacity:0; transform:scale(0.95) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .animate-modal-in { animation: modalIn 0.18s ease-out both; }
        .animate-slide-up { animation: slideUp 0.2s ease-out both; }
      `}</style>

      <div className="space-y-5 p-4 sm:p-6 max-w-7xl mx-auto">

        {/* ── Stat chips ── */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-teal-500 p-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0"><ImageIcon className="w-4 h-4 text-teal-700" /></div>
            <div><p className="text-xl font-bold text-teal-700">{banners.length}</p><p className="text-xs text-gray-500">Total Banners</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-green-500 p-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0"><Check className="w-4 h-4 text-green-600" /></div>
            <div><p className="text-xl font-bold text-green-600">{activeCount}</p><p className="text-xs text-gray-500">Active</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-red-400 p-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0"><AlertTriangle className="w-4 h-4 text-red-500" /></div>
            <div><p className="text-xl font-bold text-red-500">{inactiveCount}</p><p className="text-xs text-gray-500">Inactive</p></div>
          </div>
        </div>

        {/* ── Search + Filter ── */}
        {banners.length > 0 && (
          <div className="rounded-2xl border border-[#e8e0d0] overflow-hidden bg-white">
          <div className="px-4 sm:px-5 py-3 flex items-center gap-3">
  <div className="relative flex-1">
    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    <input
      type="text"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Search by title, filename or ID…"
      className="w-full pl-9 pr-9 py-2 rounded-lg border border-[#e2ddd4] text-sm bg-white text-gray-700 placeholder-gray-400 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
    />
    {search && (
      <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
        <X size={13} />
      </button>
    )}
  </div>
  <button
    onClick={() => setFiltersOpen((v) => !v)}
    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all flex-shrink-0
      ${filtersOpen ? "bg-teal-50 border-teal-300 text-teal-700" : "bg-white border-[#e2ddd4] text-gray-600 hover:border-teal-300 hover:text-teal-700"}`}
  >
    <Filter size={14} />
    <span className="hidden sm:inline">Filters</span>
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`}>
      <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
  <button onClick={() => setAddOpen(true)}
    className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white px-3 sm:px-4 py-2 rounded-lg font-semibold text-sm transition-colors shadow-sm flex-shrink-0">
    <Plus size={15} /><span className="hidden sm:inline">Add Banner</span>
  </button>
</div>

            {filtersOpen && (
              <div className="px-4 sm:px-5 pb-4 pt-0 border-t border-gray-100 bg-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                      className="h-10 px-3 rounded-xl border border-[#e2ddd4] text-sm bg-white text-gray-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all cursor-pointer">
                      <option value="all">All Banners ({banners.length})</option>
                      <option value="active">Active ({activeCount})</option>
                      <option value="inactive">Inactive ({inactiveCount})</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sort By</label>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                      className="h-10 px-3 rounded-xl border border-[#e2ddd4] text-sm bg-white text-gray-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all cursor-pointer">
                      <option value="">Default</option>
                      <option value="title_asc">Title A–Z</option>
                      <option value="title_desc">Title Z–A</option>
                      <option value="active">Active First</option>
                      <option value="inactive">Inactive First</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Table card ── */}
        <div className="bg-white rounded-2xl border border-[#e8e0d0] shadow-sm overflow-hidden">
          {bannersLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={28} className="animate-spin text-teal-500" />
            </div>
          ) : banners.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <div className="w-14 h-14 rounded-2xl bg-teal-50 border border-teal-200 flex items-center justify-center mb-4">
                <ImageIcon size={26} className="text-teal-400" />
              </div>
              <p className="text-sm font-semibold text-gray-600 mb-1">No banners yet</p>
              <p className="text-xs text-gray-400">Click "+ Add Banner" above to get started</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <Search size={22} className="text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No results for "<span className="font-medium">{search}</span>"</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-teal-700 text-white">
                    <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wider w-12 sm:w-16">ID</th>
                    <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wider w-28 sm:w-40">Image</th>
                    <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wider">Title / File</th>
                    <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Status</th>
                    <th className="text-right px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedBan.map((item, idx) => (
                    <tr key={item.id} className={`transition-colors hover:bg-teal-50/40 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                      {/* ID */}
                      <td className="px-4 sm:px-5 py-3 sm:py-4 text-sm text-gray-500">{idx + 1}</td>

                      {/* Thumbnail */}
                      <td className="px-4 sm:px-5 py-3 sm:py-4">
                        <div
                          className="relative w-20 sm:w-28 h-12 sm:h-16 rounded-xl overflow-hidden border border-[#e8e0d0] bg-white cursor-pointer group/img"
                          onClick={() => setViewItem(item)}
                        >
                          <img
                            src={getBannerImageUrl(item.banner)}
                            alt={item.title || `Banner ${item.id}`}
                            className="w-full h-full object-cover transition-transform duration-200 group-hover/img:scale-105"
                            onError={(e) => { e.target.src = "https://placehold.co/200x100?text=No+Image"; }}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/30 transition-all flex items-center justify-center">
                            <ZoomIn size={14} className="text-white opacity-0 group-hover/img:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </td>

                      {/* Title / Filename */}
                      <td className="px-4 sm:px-5 py-3 sm:py-4">
                        {item.title ? (
                          <p className="text-sm font-medium text-gray-800 line-clamp-1">{item.title}</p>
                        ) : (
                          <p className="text-sm text-gray-400 italic">No title</p>
                        )}
                       
                        {/* Status badge — mobile only */}
                        <span className={`sm:hidden mt-1 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full
                          ${toActive(item.status)
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-red-50 text-red-500 border border-red-200"}`}>
                          {toActive(item.status) ? "Active" : "Inactive"}
                        </span>
                      </td>

                      {/* Status toggle — desktop */}
                      <td className="px-4 sm:px-5 py-3 sm:py-4 hidden sm:table-cell">
                        <StatusToggle
                          active={toActive(item.status)}
                          onChange={() => handleToggle(item)}
                          disabled={togglingId === item.id}
                        />
                      </td>

                      {/* Actions */}
                      <td className="px-4 sm:px-5 py-3 sm:py-4">
                        <div className="flex items-center justify-end gap-1.5 sm:gap-2">
                          {/* Toggle on mobile */}
                          <div className="sm:hidden">
                            <StatusToggle
                              active={toActive(item.status)}
                              onChange={() => handleToggle(item)}
                              disabled={togglingId === item.id}
                            />
                          </div>
                          <button onClick={() => setViewItem(item)} title="View banner"
                            className="p-1.5 sm:p-2 rounded-lg text-teal-600 hover:text-teal-800 hover:bg-teal-50 transition-all active:scale-95">
                            <Eye size={14} />
                          </button>
                          <button onClick={() => setDelItem(item)} title="Delete banner"
                            className="p-1.5 sm:p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all active:scale-95">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
             <div className="px-4 border-t border-gray-100">
  <Pagination currentPage={banPage} totalPages={banTotalPages} totalItems={banTotal}
    pageSize={banSize} onPageChange={setBanPage} onPageSizeChange={setBanSize} />
</div>
            </div>
          )}

         
        </div>
      </div>

      <AddModal    open={addOpen}    onClose={() => setAddOpen(false)} onSave={handleAdd} />
      <ViewModal   open={!!viewItem} onClose={() => setViewItem(null)} item={viewItem} getImageUrl={getBannerImageUrl} />
      <DeleteModal open={!!delItem}  onClose={() => setDelItem(null)}  onConfirm={handleDelete} item={delItem} />
      <ToastContainer toasts={toasts} />
    </>
  );
}